<template>
  <div class="case-detail-page" v-if="caseItem">
    <section class="detail-hero" aria-labelledby="case-title">
      <img :src="caseItem.image" :alt="caseItem.title + '服务场景示意图'" />
      <div class="detail-hero-mask"></div>
      <div class="detail-hero-content">
        <nav class="breadcrumb" aria-label="面包屑">
          <router-link to="/">首页</router-link>
          <span>/</span>
          <router-link to="/cases">案例中心</router-link>
          <span>/</span>
          <span>{{ caseItem.title }}</span>
        </nav>
        <p class="eyebrow">Project Case</p>
        <h1 id="case-title">{{ caseItem.title }}</h1>
        <p>
          {{ caseItem.city }}{{ caseItem.projectType }}项目，服务内容包括{{ caseItem.services.join("、") }}。
        </p>
        <router-link class="btn btn-primary" :to="{ path: '/', hash: '#lead-form' }">咨询类似项目方案</router-link>
      </div>
    </section>

    <section class="section">
      <div class="section-inner summary-grid">
        <article class="summary-card">
          <span>项目城市</span>
          <strong>{{ caseItem.city }}</strong>
        </article>
        <article class="summary-card">
          <span>项目类型</span>
          <strong>{{ caseItem.projectType }}</strong>
        </article>
        <article class="summary-card">
          <span>服务对象</span>
          <strong>{{ caseItem.serviceObject }}</strong>
        </article>
      </div>
    </section>

    <section class="section muted">
      <div class="section-inner content-layout">
        <article class="content-main">
          <div class="block">
            <p class="eyebrow">Background</p>
            <h2>项目背景</h2>
            <p>{{ caseItem.background }}</p>
          </div>

          <div class="block">
            <p class="eyebrow">Service Content</p>
            <h2>服务内容</h2>
            <div class="tag-list">
              <span v-for="item in caseItem.services" :key="item">{{ item }}</span>
            </div>
          </div>

          <div class="block">
            <p class="eyebrow">Inspection Scope</p>
            <h2>检测范围</h2>
            <ul class="check-list">
              <li v-for="item in caseItem.scope" :key="item">
                <i class="el-icon-check"></i>
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>

          <div class="block">
            <p class="eyebrow">Workflow</p>
            <h2>检测流程</h2>
            <ol class="process-list">
              <li v-for="(step, index) in process" :key="step.title">
                <span>{{ index + 1 }}</span>
                <div>
                  <h3>{{ step.title }}</h3>
                  <p>{{ step.description }}</p>
                </div>
              </li>
            </ol>
          </div>

          <div class="block">
            <p class="eyebrow">Typical Issues</p>
            <h2>典型问题类型</h2>
            <ul class="check-list">
              <li v-for="item in caseItem.typicalProblems" :key="item">
                <i class="el-icon-warning-outline"></i>
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>

          <div class="block">
            <p class="eyebrow">Deliverables</p>
            <h2>交付成果</h2>
            <p>{{ caseItem.result }}</p>
            <div class="deliver-tags">
              <span v-for="item in caseItem.deliverables" :key="item">{{ item }}</span>
            </div>
          </div>

          <div class="block value-block">
            <p class="eyebrow">Project Value</p>
            <h2>项目价值</h2>
            <p>{{ caseItem.value }}</p>
          </div>
        </article>

        <aside class="side-panel">
          <h2>相关服务</h2>
          <router-link
            v-for="service in relatedServices"
            :key="service.slug"
            :to="service.path"
          >
            <strong>{{ service.shortTitle }}</strong>
            <span>{{ service.summary }}</span>
          </router-link>
          <router-link class="side-cta" :to="{ path: '/', hash: '#lead-form' }">获取项目检测方案</router-link>
        </aside>
      </div>
    </section>

    <div class="beian">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">苏ICP备2024065706号</a>
      <a href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=32050602013590" target="_blank" rel="noopener">苏公网安备32050602013590号</a>
    </div>
  </div>
</template>

<script>
import { detailedCases, serviceProcessDetails, services } from "../data/services";
import {
  absoluteUrl,
  breadcrumbSchema,
  localBusinessSchema,
  organizationSchema,
  setSeo,
  setStructuredData
} from "../utils/seo";

export default {
  name: "CaseDetails",
  data() {
    return {
      process: serviceProcessDetails
    };
  },
  computed: {
    caseItem() {
      const id = Number(this.$route.params.id);
      return detailedCases.find(function(item) {
        return item.id === id;
      });
    },
    relatedServices() {
      if (!this.caseItem) {
        return [];
      }
      return services.filter(service => this.caseItem.relatedServices.indexOf(service.slug) !== -1);
    }
  },
  mounted() {
    this.applyPageSeo();
  },
  watch: {
    "$route.params.id"() {
      this.applyPageSeo();
    }
  },
  methods: {
    applyPageSeo() {
      if (!this.caseItem) {
        this.$router.replace("/cases");
        return;
      }

      const description =
        this.caseItem.city +
        this.caseItem.projectType +
        "服务案例，服务内容包括" +
        this.caseItem.services.join("、") +
        "，交付成果包括" +
        this.caseItem.deliverables.join("、") +
        "。";

      setSeo({
        title: this.caseItem.title + "｜江苏道鼎升工程检测案例",
        description: description,
        path: "/cases/" + this.caseItem.id,
        image: this.caseItem.image,
        type: "article"
      });

      setStructuredData([
        organizationSchema(),
        localBusinessSchema(),
        {
          "@type": "Article",
          headline: this.caseItem.title,
          description: description,
          image: absoluteUrl(this.caseItem.image),
          mainEntityOfPage: absoluteUrl("/cases/" + this.caseItem.id),
          publisher: {
            "@id": absoluteUrl("/#organization")
          },
          about: this.caseItem.services
        },
        breadcrumbSchema([
          { name: "首页", path: "/" },
          { name: "案例中心", path: "/cases" },
          { name: this.caseItem.title, path: "/cases/" + this.caseItem.id }
        ])
      ]);
    }
  }
};
</script>

<style lang="scss" scoped>
.case-detail-page {
  background: #f5f8f7;
  color: #1f2d3d;
}

.detail-hero {
  min-height: 460px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.detail-hero img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-hero-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(12, 38, 51, 0.94), rgba(15, 76, 92, 0.72));
}

.detail-hero-content,
.section-inner {
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
}

.detail-hero-content {
  position: relative;
  z-index: 1;
  color: #fff;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 26px;
  color: rgba(255, 255, 255, 0.78);
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
}

.eyebrow {
  color: #d76b30;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.detail-hero h1 {
  max-width: 880px;
  font-size: 42px;
  line-height: 1.25;
  margin: 0 0 18px;
}

.detail-hero p {
  max-width: 860px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 17px;
  line-height: 1.9;
}

.btn,
.side-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 22px;
  border-radius: 4px;
  text-decoration: none;
}

.btn-primary,
.side-cta {
  color: #fff;
  background: #18736e;
}

.section {
  padding: 72px 0;
}

.muted {
  background: #fff;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.summary-card,
.content-main,
.side-panel {
  border: 1px solid #dce6e4;
  border-radius: 8px;
  background: #fff;
}

.summary-card {
  padding: 24px;
}

.summary-card span {
  display: block;
  color: #5c6873;
  margin-bottom: 10px;
}

.summary-card strong {
  color: #0f4c5c;
  font-size: 20px;
  line-height: 1.5;
}

.content-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
  align-items: start;
}

.content-main {
  padding: 34px;
}

.block + .block {
  margin-top: 44px;
  padding-top: 38px;
  border-top: 1px solid #edf2f1;
}

h2 {
  color: #0f4c5c;
  font-size: 29px;
  line-height: 1.35;
  margin: 0 0 14px;
}

h3 {
  margin: 0 0 8px;
  color: #17313b;
  font-size: 18px;
}

.block p,
.process-list p {
  color: #5c6873;
  line-height: 1.85;
  margin: 0;
}

.tag-list,
.deliver-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag-list span,
.deliver-tags span {
  border-radius: 4px;
  background: #e8f3f1;
  color: #0f4c5c;
  padding: 9px 12px;
  font-weight: 700;
}

.check-list,
.process-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.check-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.check-list li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  border: 1px solid #dce6e4;
  border-radius: 8px;
  padding: 16px;
  color: #3d4b56;
  line-height: 1.7;
}

.check-list i {
  color: #18736e;
  font-size: 19px;
  margin-top: 3px;
}

.process-list {
  display: grid;
  gap: 14px;
}

.process-list li {
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 14px;
}

.process-list > li > span {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #d76b30;
  color: #fff;
  font-weight: 700;
}

.value-block {
  background: #f5f8f7;
  margin: 44px -10px 0;
  padding: 30px 28px;
  border-radius: 8px;
}

.side-panel {
  position: sticky;
  top: 84px;
  padding: 24px;
}

.side-panel h2 {
  font-size: 24px;
}

.side-panel a {
  display: block;
  text-decoration: none;
  color: inherit;
}

.side-panel a:not(.side-cta) {
  border-top: 1px solid #edf2f1;
  padding: 16px 0;
}

.side-panel strong {
  display: block;
  color: #0f4c5c;
  margin-bottom: 8px;
}

.side-panel span {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #5c6873;
  line-height: 1.7;
}

.side-cta {
  margin-top: 18px;
}

@media (max-width: 980px) {
  .summary-grid,
  .content-layout,
  .check-list {
    grid-template-columns: 1fr;
  }

  .detail-hero h1 {
    font-size: 34px;
  }

  .side-panel {
    position: static;
  }
}

@media (max-width: 640px) {
  .detail-hero-content,
  .section-inner {
    width: min(100% - 28px, 1180px);
  }

  .detail-hero {
    min-height: auto;
    padding: 70px 0;
  }

  .detail-hero h1 {
    font-size: 28px;
  }

  .section {
    padding: 52px 0;
  }

  .content-main {
    padding: 24px 18px;
  }
}
</style>
